import { BadgeProps, chakra, HStack, Tooltip } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import { EnvironmentPipelineStageStateEnum, EnvironmentPipelineStateEnum } from "@/__generated__/graphql";
import BadgeDot from "@/components/shared/BadgeDot";
import StateBadge from "@/components/StateBadges/StateBadge";

const EnvironmentDeployStateBadge_Stage = graphql(`
  fragment EnvironmentDeployStateBadge_Stage on EnvironmentPipelineStage {
    id
    state
  }
`);

export function badgeDotForState(state: EnvironmentPipelineStateEnum) {
  switch (state) {
    case EnvironmentPipelineStateEnum.Ready:
    case EnvironmentPipelineStateEnum.Running:
      return <BadgeDot variant="yellow" />;
    case EnvironmentPipelineStateEnum.Done:
      return <BadgeDot variant="green" />;
    case EnvironmentPipelineStateEnum.Aborted:
    case EnvironmentPipelineStateEnum.Errored:
      return <BadgeDot variant="red" />;
  }
}

export function stateForDisplay(state: EnvironmentPipelineStateEnum): string {
  switch (state) {
    case EnvironmentPipelineStateEnum.Ready:
      return "Waiting";
    default:
      return state;
  }
}

type Props = {
  state?: EnvironmentPipelineStateEnum;
  stages?: FragmentType<typeof EnvironmentDeployStateBadge_Stage>[];
  statusReason?: string | null;
};
export default function EnvironmentDeployStateBadge({
  state,
  stages: propsStages,
  statusReason,
  ...props
}: Props & BadgeProps) {
  if (typeof state === "undefined") return null;

  const stages = getFragmentData(EnvironmentDeployStateBadge_Stage, propsStages);

  let stageContent = null;
  if (stages) {
    const color = (state: EnvironmentPipelineStageStateEnum) => {
      switch (state) {
        case EnvironmentPipelineStageStateEnum.Ready:
          return "transparent";
        case EnvironmentPipelineStageStateEnum.Running:
          return "text.lightest";
        case EnvironmentPipelineStageStateEnum.Done:
          return "success";
        case EnvironmentPipelineStageStateEnum.Aborted:
        case EnvironmentPipelineStageStateEnum.Errored:
          return "error";
      }
    };
    stageContent = (
      <HStack
        backgroundColor="background.darkest"
        bottom={0}
        left={0}
        position="absolute"
        spacing={0}
        w="full"
      >
        {stages.map(stage => (
          <chakra.span
            key={stage.id}
            backgroundColor={color(stage.state)}
            flex={1}
            height="3px"
            {...(stage.state === EnvironmentPipelineStageStateEnum.Running ? { className: "anim-fade" } : {})}
          />
        ))}
      </HStack>
    );
  }

  const badge = (
    <StateBadge
      badgeDot={badgeDotForState(state)}
      overflow="hidden"
      position="relative"
      spinnerVisibleOnStates={[EnvironmentPipelineStateEnum.Running]}
      state={stateForDisplay(state)}
      {...props}
    >
      {stageContent}
    </StateBadge>
  );

  if (statusReason) {
    return (
      <Tooltip hasArrow label={statusReason} placement="top">
        <span>{badge}</span>
      </Tooltip>
    );
  }

  return badge;
}
